import { useEffect, useRef, useState } from 'react';
import { CloudOff, KeyRound, RefreshCw, Settings as SettingsIcon } from 'lucide-react';
import type { User } from '@milk/shared';
import { api, ApiError, request, type SetupInfo } from './lib/api';
import { isNative, setAuthToken } from './lib/config';
import { startStore, stopStore, useStore } from './store/store';
import { onNativeCommand, postNative } from './lib/native';
import { passkeyAutofillSupported, passkeyLogin, passkeysSupported, wasCancelled } from './lib/passkeys';
import { startPresence, stopPresence } from './lib/presence';
import { linkProps, match, navigate, useLocation } from './lib/router';
import { Empty, Logo, Session, Spinner, ToastHost, useSubmit, Wordmark } from './components/ui';
import { ListScreen } from './screens/ListScreen';
import { StoreEditor, StoresScreen } from './screens/Stores';
import { RememberedScreen } from './screens/Remembered';
import { Settings } from './screens/Settings';

export function App() {
  return <ToastHost><Root /></ToastHost>;
}

// The signed-in person and server info, so the app can open offline.
interface SavedSession { setup: SetupInfo; user: User }
const SESSION_KEY = 'milk.session';
function saveSession(s: SavedSession) { try { localStorage.setItem(SESSION_KEY, JSON.stringify(s)); } catch { /* storage blocked */ } }
function readSession(): SavedSession | null { try { return JSON.parse(localStorage.getItem(SESSION_KEY) ?? 'null'); } catch { return null; } }
function clearSession() { try { localStorage.removeItem(SESSION_KEY); } catch { /* storage blocked */ } }

function Root() {
  const [setup, setSetup] = useState<SetupInfo | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [checked, setChecked] = useState(false);

  const load = async () => {
    setError(null);
    try {
      const s = await api.setup();
      setSetup(s);
      if (!s.needsSetup) {
        try {
          const me = await api.me();
          setUser(me.user);
          saveSession({ setup: s, user: me.user });
        } catch (e) {
          if (e instanceof ApiError && e.status === 401) clearSession();
          else throw e;
        }
      }
    } catch (e) {
      // can't reach the server: open the offline copy if this device has signed in before
      const saved = readSession();
      if (e instanceof ApiError && e.status === 0 && saved) { setSetup(saved.setup); setUser(saved.user); }
      else setError((e as Error).message);
    } finally {
      setChecked(true);
      postNative({ type: 'ready' });
    }
  };
  useEffect(() => { void load(); }, []);
  useEffect(() => {
    if (!user) return;
    void startStore(user).then(() => startPresence());
    return () => stopPresence();
  }, [user]);
  // the server says the session is gone (signed out elsewhere, password changed)
  useEffect(() => {
    const on = () => { void finishSignOut(); };
    window.addEventListener('milk:signed-out', on);
    return () => window.removeEventListener('milk:signed-out', on);
  });

  const signedIn = (u: User, token?: string) => {
    if (token) { setAuthToken(token); postNative({ type: 'sessionChanged', token }); }
    setUser(u);
    setSetup((s) => { const next = s ? { ...s, needsSetup: false } : s; if (next) saveSession({ setup: next, user: u }); return next; });
    if (location.pathname.startsWith('/invite/') || location.pathname === '/login') navigate('/', { replace: true });
  };
  const finishSignOut = async () => {
    clearSession();
    await stopStore();
    setAuthToken(null);
    postNative({ type: 'logout' });
    setUser(null);
    navigate('/', { replace: true });
  };
  const signOut = async () => {
    try { await request('POST', '/api/v1/auth/logout', {}); } catch { /* already gone or offline */ }
    await finishSignOut();
  };

  if (!checked) return <div className="center full"><Spinner label="Loading Milk Maybe?…" /></div>;
  if (error || !setup) {
    return (
      <div className="auth">
        <Empty title="Can’t reach Milk Maybe?">
          <p>{error}</p>
          <button className="btn primary" onClick={() => void load()}>Try again</button>
        </Empty>
      </div>
    );
  }
  if (!user) return <AuthScreens setup={setup} onSignedIn={signedIn} />;
  return (
    <Session.Provider value={{ user, setUser, signOut }}>
      <Shell />
    </Session.Provider>
  );
}

// ---------- signed out

function AuthScreens({ setup, onSignedIn }: { setup: SetupInfo; onSignedIn(u: User, token?: string): void }) {
  const { path } = useLocation();
  const invite = match('/invite/:token', path);
  if (setup.needsSetup) return <CreateAdmin onSignedIn={onSignedIn} />;
  if (invite) return <AcceptInvite token={invite.token} onSignedIn={onSignedIn} />;
  return <Login onSignedIn={onSignedIn} />;
}

const client = isNative ? 'native' : undefined;

function AuthFrame({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="auth">
      <div className="auth-card">
        <div className="auth-brand"><Logo size={56} /><Wordmark /></div>
        <h1>{title}</h1>
        {subtitle && <p className="muted">{subtitle}</p>}
        {children}
      </div>
    </div>
  );
}

function Login({ onSignedIn }: { onSignedIn(u: User, t?: string): void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const s = useSubmit();
  const autofill = useRef<AbortController | null>(null);

  // Offer passkeys right in the email field's autofill menu (1Password, iCloud Keychain, Chrome…).
  const startAutofill = () => {
    void passkeyAutofillSupported().then((ok) => {
      if (!ok) return;
      autofill.current?.abort();
      const ac = new AbortController();
      autofill.current = ac;
      passkeyLogin(client, { conditional: true, signal: ac.signal })
        .then((r) => onSignedIn(r.user, r.token))
        .catch(() => { /* autofill passkeys fail quietly: the person didn't ask for anything yet */ });
    });
  };
  useEffect(() => { startAutofill(); return () => autofill.current?.abort(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const withPasskey = () => {
    autofill.current?.abort();
    void s.run(async () => {
      try { const r = await passkeyLogin(client); onSignedIn(r.user, r.token); }
      catch (e) { if (!wasCancelled(e)) throw e; } finally { startAutofill(); }
    });
  };

  return (
    <AuthFrame title="Sign in" subtitle="Ask the owner for an invite.">
      <form className="stack" onSubmit={(e) => { e.preventDefault(); void s.run(async () => { const r = await api.login({ email, password, client }); onSignedIn(r.user, r.token); }); }}>
        <input type="email" name="email" autoComplete="username webauthn" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Email" autoFocus />
        <input type="password" name="password" autoComplete="current-password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} aria-label="Password" />
        {s.error && <p className="error" role="alert">{s.error}</p>}
        <button className="btn primary big" disabled={s.busy || !email || !password}>{s.busy ? 'Signing in…' : 'Sign in'}</button>
        {passkeysSupported() && (
          <button type="button" className="btn big" disabled={s.busy} onClick={withPasskey}><KeyRound size={18} aria-hidden /> Sign in with a passkey</button>
        )}
        {isNative && <button type="button" className="btn ghost" onClick={() => postNative({ type: 'changeServer' })}>Change server</button>}
      </form>
      <InviteLinkEntry />
    </AuthFrame>
  );
}

/** Paste an invite link (handy in the apps, where links from messages open in the browser). */
function InviteLinkEntry() {
  const [open, setOpen] = useState(false);
  const [link, setLink] = useState('');
  const token = /mmi_[A-Za-z0-9_-]+/.exec(link)?.[0];
  if (!open) return <p className="muted small invite-hint"><button className="link" onClick={() => setOpen(true)}>Have an invite link?</button></p>;
  return (
    <form className="stack invite-hint" onSubmit={(e) => { e.preventDefault(); if (token) navigate(`/invite/${token}`); }}>
      <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="Paste the invite link" aria-label="Invite link" autoFocus />
      <button className="btn" disabled={!token}>Use invite</button>
    </form>
  );
}

function CreateAdmin({ onSignedIn }: { onSignedIn(u: User, t?: string): void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const s = useSubmit();
  return (
    <AuthFrame title="Welcome to Milk Maybe?" subtitle="Create the first account. You'll be the admin and can invite everyone else.">
      <form className="stack" onSubmit={(e) => { e.preventDefault(); void s.run(async () => { const r = await api.createAdmin({ name, email, password, client }); onSignedIn(r.user, r.token); }); }}>
        <input placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} aria-label="Your name" autoFocus />
        <input type="email" autoComplete="username" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Email" />
        <input type="password" autoComplete="new-password" placeholder="Password (8+ characters)" value={password} onChange={(e) => setPassword(e.target.value)} aria-label="Password" />
        {s.error && <p className="error" role="alert">{s.error}</p>}
        <button className="btn primary big" disabled={s.busy || !email || password.length < 8}>{s.busy ? 'Creating…' : 'Create admin account'}</button>
      </form>
    </AuthFrame>
  );
}

function AcceptInvite({ token, onSignedIn }: { token: string; onSignedIn(u: User, t?: string): void }) {
  const [inv, setInv] = useState<{ email: string | null } | null | 'bad'>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const s = useSubmit();
  useEffect(() => {
    api.invite(token).then((i) => { setInv(i); if (i.email) setEmail(i.email); }).catch(() => setInv('bad'));
  }, [token]);
  if (inv === null) return <div className="center full"><Spinner /></div>;
  if (inv === 'bad') {
    return (
      <AuthFrame title="This invite doesn't work" subtitle="It may have been used already or expired. Ask for a new link.">
        <a className="btn" {...linkProps('/login')}>Sign in instead</a>
      </AuthFrame>
    );
  }
  return (
    <AuthFrame title="You're invited" subtitle="Create your account to share the shopping list.">
      <form className="stack" onSubmit={(e) => { e.preventDefault(); void s.run(async () => { const r = await api.signup({ name, email, password, inviteToken: token, client }); onSignedIn(r.user, r.token); }); }}>
        <input placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} aria-label="Your name" autoFocus />
        <input type="email" autoComplete="username" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} readOnly={!!inv.email} aria-label="Email" />
        <input type="password" autoComplete="new-password" placeholder="Password (8+ characters)" value={password} onChange={(e) => setPassword(e.target.value)} aria-label="Password" />
        {s.error && <p className="error" role="alert">{s.error}</p>}
        <button className="btn primary big" disabled={s.busy || !email || password.length < 8}>{s.busy ? 'Creating…' : 'Create account'}</button>
      </form>
    </AuthFrame>
  );
}

// ---------- signed in

function Shell() {
  const { path } = useLocation();
  useEffect(() => onNativeCommand((c) => { if (c.type === 'navigate') navigate(c.path); }), []);

  let screen: React.ReactNode;
  let m: Record<string, string> | null;
  if (path === '/' || path === '/login' || path.startsWith('/invite/')) screen = <ListScreen />;
  else if (path === '/stores') screen = <StoresScreen />;
  else if ((m = match('/stores/:id', path))) screen = <StoreEditor id={m.id} key={m.id} />;
  else if (path === '/remembered') screen = <RememberedScreen />;
  else if (path === '/settings') screen = <Settings />;
  else screen = <Empty title="Page not found"><a className="btn" {...linkProps('/')}>Back to the list</a></Empty>;

  return (
    <div className="shell">
      <a className="skip-link" href="#main">Skip to the list</a>
      <header className="topbar">
        <a className="brand" {...linkProps('/')}><Logo /> <Wordmark /></a>
        <nav aria-label="App">
          <SyncStatus />
          <a className={`icon-btn${path === '/settings' ? ' on' : ''}`} {...linkProps('/settings')} aria-label="Settings" aria-current={path === '/settings' ? 'page' : undefined}><SettingsIcon size={22} aria-hidden /></a>
        </nav>
      </header>
      <main id="main" tabIndex={-1}>{screen}</main>
    </div>
  );
}

/** "Offline · 2 changes waiting" / "Syncing…" in the top bar (nothing when all is well). */
function SyncStatus() {
  const online = useStore((s) => s.online);
  const pending = useStore((s) => s.pending);
  const syncing = useStore((s) => s.syncing);
  const complete = useStore((s) => s.complete);
  if (online && !pending && (complete || !syncing)) return null;
  const waiting = pending ? `${pending} change${pending > 1 ? 's' : ''} waiting` : '';
  return (
    <a className={`sync-pill${online ? '' : ' offline'}`} {...linkProps('/settings#offline')} role="status"
      title={online ? 'Syncing with the server' : 'Not connected. Changes are saved on this device and sent when you’re back online.'}>
      {online ? <RefreshCw size={14} aria-hidden className={syncing ? 'spin' : ''} /> : <CloudOff size={14} aria-hidden />}
      <span>{online ? (syncing && !complete ? 'Downloading…' : waiting || 'Syncing…') : `Offline${waiting ? ` · ${waiting}` : ''}`}</span>
    </a>
  );
}
